
import React from "react";
import { Movie } from "@/types/movie";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Undo } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Muted, Span } from "@/components/ui/typography";
import { UNDO_DURATION } from "@/context/movie/movieUtils";

interface UndoCardGridProps {
  movie: Movie;
  getActionText: () => string;
  handleUndo: (e: React.MouseEvent) => void;
  progress: number;
  isExpiring: boolean;
}

const UndoCardGrid = ({
  movie,
  getActionText,
  handleUndo,
  progress,
  isExpiring
}: UndoCardGridProps) => {
  // Destructure properties we need from the movie object
  const { title, year, director, tags } = movie;
  
  // If this card is in the process of expiring, add a special class
  // Remove duration from transition to prevent flashing
  const expiringClass = isExpiring ? 'opacity-80 scale-95' : '';

  return (
    <div className={`movie-card-wrapper relative ${expiringClass}`}>
      <div className="relative overflow-hidden rounded-lg bg-mono-eerieBlack border-0 shadow-xl">
        <AspectRatio ratio={2/3} className="w-full">
          <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
            {/* Movie title centered both vertically and horizontally */}
            <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center">
              <Muted weight="medium" className="text-center px-4">
                {title}
              </Muted>
            </div>
            
            {/* Action text and undo button grouped together - updated size to match other buttons */}
            <div className="absolute bottom-6 flex flex-col items-center">
              <Span size="small" variant="muted" className="italic mb-3">
                {getActionText()}
              </Span>

              <Button 
                onClick={handleUndo} 
                variant="gridButtonUndo"
                size="badge"
                title="Undo"
                className="group text-mono-frenchGray2"
              >
                <Undo className="group-hover:stroke-mono-aurora" />
                <span>Undo</span>
              </Button>
            </div>
            
            {/* Progress bar showing time remaining - CSS animated */}
            <div className="absolute bottom-0 left-0 right-0 h-1">
              <Progress 
                value={progress} 
                className="h-1 rounded-none bg-mono-outerSpace/20" 
                indicatorClassName="bg-mono-outerSpace/60" 
                transitionDuration={UNDO_DURATION}
              />
            </div>
          </div>
        </AspectRatio>
      </div>
    </div>
  );
};

export default UndoCardGrid;
